/**
 * Dummy data + column config cho dashboard "Commercial Position" (PnL).
 * Số liệu transcribe từ `plan/PBI_Dashboard.md` §4. Tách khỏi UI — sau này thay
 * bằng RTK Query endpoint (`store/api/...`) là xong, KHÔNG đụng UI.
 *
 * Quy ước số âm: để giá trị âm (vd -94966); PnLTable tự render ngoặc + đỏ.
 * Ô trống trong doc → `null`.
 */
import type { PnLColumn, PnLHeaderGroup, PnLRow } from './PnLTable'

export interface PnLTableData {
  columns: PnLColumn[]
  rows: PnLRow[]
  headerGroups?: PnLHeaderGroup[]
}

// ── CỘT TRÁI ────────────────────────────────────────────────────────────────
// A) Purchases (Kg)
export const purchasesTable: PnLTableData = {
  columns: [
    { key: 'type', label: 'Type', text: true },
    { key: 'contracted', label: 'Contracted' },
    { key: 'received', label: 'Received' },
    { key: 'toReceive', label: 'To receive' },
    { key: 'fixed', label: 'Fixed' },
    { key: 'toBeFix', label: 'To be fix' },
  ],
  rows: [
    { type: 'Consignment', contracted: 24038338, received: 16565940, toReceive: 7472398, fixed: 15337198, toBeFix: 1268837 },
    { type: 'Differential', contracted: 86771517, received: 86159885, toReceive: 503971, fixed: 83851517, toBeFix: 2920000 },
    { type: 'Outright', contracted: 405017046, received: 403543975, toReceive: 194138, fixed: 0, toBeFix: 0 },
    { type: 'Total', contracted: 515826901, received: 506269799, toReceive: 698109, fixed: 99188715, toBeFix: 4188837, isTotal: true },
  ],
}

// B) Sales (Kg)
export const salesTable: PnLTableData = {
  columns: [
    { key: 'type', label: 'Type', text: true },
    { key: 'contracted', label: 'Contracted' },
    { key: 'delivered', label: 'Delivered' },
    { key: 'toBeShipped', label: 'To be shipped' },
    { key: 'fixed', label: 'Fixed' },
    { key: 'toBeFix', label: 'To be fix' },
  ],
  rows: [
    { type: 'Onshore', contracted: 132671955, delivered: 128116127, toBeShipped: 4555828, fixed: 128144183, toBeFix: 4527772 },
    { type: 'Offshore', contracted: 356356555, delivered: 329635302, toBeShipped: 26721253, fixed: 346964762, toBeFix: 9391793 },
    { type: 'Total', contracted: 489028510, delivered: 457751429, toBeShipped: 31277081, fixed: 475108945, toBeFix: 13919565, isTotal: true },
  ],
}

// C) Futures (Lot) — doc chỉ cho 1 phần dòng; data thật nhiều hơn. TODO: verify đủ tháng.
export const futuresTable: PnLTableData = {
  columns: [
    { key: 'month', label: 'Month', text: true },
    { key: 'totalBought', label: 'Total bought' },
    { key: 'totalSold', label: 'Total sold' },
    { key: 'ptbfPurchase', label: 'PTBF purchase' },
    { key: 'ptbfSale', label: 'PTBF sale' },
    { key: 'openPosition', label: 'Open position' },
  ],
  rows: [
    { month: 'LRCX21', totalBought: 8, totalSold: -8, ptbfPurchase: null, ptbfSale: null, openPosition: null },
    { month: 'LRCF22', totalBought: 63, totalSold: -63, ptbfPurchase: null, ptbfSale: null, openPosition: 0 },
    { month: 'LRCH22', totalBought: 1374, totalSold: -1374, ptbfPurchase: 0, ptbfSale: 0, openPosition: 0 },
    { month: 'LRCK22', totalBought: 2762, totalSold: -3187, ptbfPurchase: 0, ptbfSale: 0, openPosition: -425 },
    { month: 'Total', totalBought: 70853, totalSold: -71655, ptbfPurchase: -292, ptbfSale: 892, openPosition: -202, isTotal: true },
  ],
}

// D) Inventory (Kg)
export const inventoryTable: PnLTableData = {
  columns: [
    { key: 'grade', label: 'Grade', text: true },
    { key: 'receiveFrP', label: 'Receive fr. P' },
    { key: 'issueToPro', label: 'Issue to Pro.' },
    { key: 'receiveFrPro', label: 'Receive fr. Pro.' },
    { key: 'shipped', label: 'Shipped' },
    { key: 'stock', label: 'Stock' },
  ],
  rows: [
    { grade: 'FAQ', receiveFrP: 50780685, issueToPro: 51461181, receiveFrPro: 1602539, shipped: 919884, stock: 0 },
    { grade: 'Grade 2', receiveFrP: 107666223, issueToPro: 52421085, receiveFrPro: 68510688, shipped: 118254106, stock: 6118801 },
    { grade: 'G1-Sc16', receiveFrP: 152830505, issueToPro: 128285432, receiveFrPro: 142494180, shipped: 159876373, stock: 7263141 },
    { grade: 'G1-Sc17', receiveFrP: 0, issueToPro: 1228644, receiveFrPro: 1566409, shipped: 337765, stock: 0 },
    { grade: 'G1-Sc18', receiveFrP: 29016187, issueToPro: 49386276, receiveFrPro: 61002555, shipped: 38075901, stock: 2483046 },
    { grade: 'G1-Sc19', receiveFrP: 0, issueToPro: 211908, receiveFrPro: 406690, shipped: 194782, stock: 0 },
    { grade: 'G1-Sc20', receiveFrP: 34924, issueToPro: 409379, receiveFrPro: 644865, shipped: 270370, stock: 0 },
    { grade: 'Grade 3', receiveFrP: 1165110, issueToPro: 1678778, receiveFrPro: 3999651, shipped: 3426129, stock: 56666 },
    { grade: 'Others', receiveFrP: 1524613, issueToPro: 11053289, receiveFrPro: 12708015, shipped: 2455146, stock: 674233 },
    { grade: 'WIP', receiveFrP: null, issueToPro: null, receiveFrPro: null, shipped: null, stock: 32409 },
    { grade: 'Total', receiveFrP: 343018247, issueToPro: 296135972, receiveFrPro: 292935592, shipped: 323810456, stock: 16628296, isTotal: true },
  ],
}

// ── CỘT GIỮA ────────────────────────────────────────────────────────────────
// E) Supplier's M2M Exposure (USD) — bảng dài, scroll dọc; ô cảnh báo ở dòng đầu.
const _SUP = (key: string, label: string): PnLColumn => ({ key, label, currency: true })
export const supplierM2MTable: PnLTableData = {
  columns: [
    { key: 'supplier', label: 'Supplier', text: true },
    // Total exp có DATA BAR (decode PBI `dataBars` 2026-06-10: maxValue=0,
    // negativeColor #F74141) — bar đỏ theo |value| âm, neo trục 0 mép phải.
    { ..._SUP('totalExp', 'Total exp'), dataBars: { negativeColor: '#F74141' } },
    _SUP('outrightExp', 'Outright exp'),
    _SUP('diffExp', 'Diff exp'),
    _SUP('insurance', 'Insurance'),
    _SUP('roomToBuy', 'Room to buy'),
  ],
  rows: [
    { supplier: 'XĂNG DẦU PACIFIC VN', totalExp: -94966, outrightExp: -94966, diffExp: null, insurance: 0, roomToBuy: -94966 },
    { supplier: 'PHI LONG', totalExp: -4722, outrightExp: 3463, diffExp: -8185, insurance: 4067, roomToBuy: 399345 },
    { supplier: 'TÁM TRÌNH', totalExp: -2635, outrightExp: 1036, diffExp: -3671, insurance: 410285, roomToBuy: 527650 },
    { supplier: 'KHÚC GIA THÀNH', totalExp: -2159, outrightExp: 133, diffExp: -2292, insurance: 176259, roomToBuy: 294100 },
    { supplier: 'HÀNG HÓA KỲ LÂN', totalExp: -1594, outrightExp: -1339, diffExp: -255, insurance: 31842, roomToBuy: 30247 },
    { supplier: 'PT. INDRA BROTHERS', totalExp: -1524, outrightExp: -1524, diffExp: null, insurance: 0, roomToBuy: -1524 },
    { supplier: 'INTIMEX ĐẮK MIL', totalExp: 112, outrightExp: 112, diffExp: null, insurance: 0, roomToBuy: 400000 },
    { supplier: 'DOUQUE COFFEE', totalExp: 113, outrightExp: 113, diffExp: null, insurance: 0, roomToBuy: 0 },
    { supplier: 'HOA TRANG', totalExp: 137, outrightExp: 137, diffExp: null, insurance: 0, roomToBuy: 400000 },
    { supplier: 'PHƯỚC SƠN LÂM ĐỒNG', totalExp: 227, outrightExp: 360, diffExp: -133, insurance: 99257, roomToBuy: 200000 },
    { supplier: 'BÌNH GIA HÂN', totalExp: 231, outrightExp: 231, diffExp: null, insurance: -11, roomToBuy: 0 },
    { supplier: 'HƯƠNG BẢN', totalExp: 270, outrightExp: 270, diffExp: null, insurance: 0, roomToBuy: 200000 },
    { supplier: 'HIỆP LOAN', totalExp: 324, outrightExp: 337, diffExp: -12, insurance: 464, roomToBuy: 200000 },
    { supplier: 'MARUBENI', totalExp: 638, outrightExp: 638, diffExp: null, insurance: 0, roomToBuy: 0 },
    { supplier: 'PHÚC MINH', totalExp: 852, outrightExp: 857, diffExp: -5, insurance: 69, roomToBuy: 200000 },
    { supplier: 'HÒA THUẬN', totalExp: 1370, outrightExp: 1362, diffExp: 8, insurance: 425781, roomToBuy: 200000 },
    { supplier: 'TUẦN HƯƠNG BẢN', totalExp: 1927, outrightExp: 2763, diffExp: -836, insurance: 720136, roomToBuy: 0 },
    { supplier: 'AN BÌNH THỊNH PHÁT', totalExp: 3947, outrightExp: 3809, diffExp: 138, insurance: 1528, roomToBuy: 0 },
    { supplier: 'Total', totalExp: -38633, outrightExp: -26133, diffExp: -12501, insurance: 4252000, roomToBuy: 6653366, isTotal: true },
  ],
}

// F) Long-Short (Kg) — header gộp 2 tầng.
export const longShortTable: PnLTableData = {
  headerGroups: [
    { label: '', span: 1 },
    { label: '2026-6', span: 3 },
    { label: 'Total', span: 3 },
  ],
  columns: [
    { key: 'grade', label: 'Grade', text: true, width: 80 },
    { key: 'unallocP', label: 'Unallocated P' },
    { key: 'unallocS', label: 'Unallocated S' },
    { key: 'cumulLS', label: 'Cumulative L-S' },
    { key: 'purchased', label: 'Purchased' },
    { key: 'sold', label: 'Sold' },
    { key: 'ls', label: 'L-S' },
  ],
  rows: [
    { grade: 'FAQ', unallocP: null, unallocS: null, cumulLS: null, purchased: 55563769, sold: -5136503, ls: 50427266 },
    { grade: 'Grade 2', unallocP: 150070, unallocS: -2419200, cumulLS: 3266601, purchased: 203119563, sold: -227018008, ls: -23898445 },
    { grade: 'G1-Sc16', unallocP: 165237, unallocS: -3683200, cumulLS: 280925, purchased: 205422208, sold: -215320728, ls: -9898520 },
    { grade: 'G1-Sc17', unallocP: null, unallocS: null, cumulLS: null, purchased: null, sold: null, ls: null },
    { grade: 'G1-Sc18', unallocP: null, unallocS: -762600, cumulLS: 1491723, purchased: 29163434, sold: -40059555, ls: -10896121 },
    { grade: 'G1-Sc19', unallocP: null, unallocS: null, cumulLS: null, purchased: null, sold: -196800, ls: -196800 },
    { grade: 'G1-Sc20', unallocP: null, unallocS: null, cumulLS: null, purchased: 34000, sold: -269600, ls: -235600 },
    { grade: 'Grade 3', unallocP: null, unallocS: -100000, cumulLS: -100000, purchased: 700000, sold: -3015960, ls: -2315960 },
    { grade: 'Others', unallocP: null, unallocS: null, cumulLS: 674233, purchased: 988500, sold: -1466490, ls: -477990 },
    { grade: 'Total', unallocP: 315307, unallocS: -6965000, cumulLS: 5613482, purchased: 494991474, sold: -492483644, ls: 2507830, isTotal: true },
  ],
}

// ── CỘT PHẢI ────────────────────────────────────────────────────────────────
// G) M2M PnL (USD) — vài dòng subtotal (in đậm + nền nhạt).
export const m2mPnlTable: PnLTableData = {
  // Cột `today`/`prev` label được PnLDashboardPage ghi đè ĐỘNG theo ngày snapshot
  // (m2mTodayDate/m2mPrevDate) BE trả về; label ở đây chỉ là fallback khi chưa có data.
  columns: [
    { key: 'label', label: '', text: true },
    { key: 'today', label: 'Latest', currency: true },
    { key: 'prev', label: 'Previous', currency: true },
    { key: 'dod', label: 'DOD change', currency: true },
  ],
  rows: [
    { label: 'Allocated PnL', today: 58011990, prev: null, dod: -58011990 },
    { label: 'Purchases PnL', today: null, prev: -731413, dod: 731413 },
    { label: 'Sales PnL', today: null, prev: 1399520, dod: -1399520 },
    { label: 'Total Physical PnL', today: 58680096, prev: null, dod: -58680096, isTotal: true },
    { label: 'London futures - hedging', today: null, prev: -17033296, dod: 17033296 },
    { label: 'NY futures - frost protection', today: null, prev: -406673, dod: 406673 },
    { label: 'Total Futures PnL', today: -17439969, prev: null, dod: 17439969, isTotal: true },
    { label: 'Net Physical PnL', today: 41646800, prev: null, dod: -41646800, isTotal: true },
    { label: 'NY futures - frost protection.', today: null, prev: -406673, dod: 406673 },
    { label: 'Net PnL', today: 41240127, prev: null, dod: -41240127, isTotal: true },
  ],
}

// H) Physical price
export const physicalPriceTable: PnLTableData = {
  columns: [
    { key: 'quality', label: 'Quality', text: true },
    { key: 'price', label: 'Price (VND/Kg)' },
    { key: 'date', label: 'Date', text: true, date: true },
  ],
  rows: [
    { quality: 'R13-ST1', price: 88700, date: '2026-05-27' },
    { quality: 'A13-WA1', price: 202000, date: '2026-05-27' },
  ],
}

// I) Exchange rate — cột đầu là SỐ: `text: false` để PnLTable format
// thousands `26,147` thay vì raw (heuristic cột đầu = text).
export const exchangeRateTable: PnLTableData = {
  columns: [
    { key: 'usdVnd', label: 'USD/VND', text: false },
    { key: 'date', label: 'Date', text: true, date: true },
  ],
  rows: [{ usdVnd: 26147, date: '2026-05-27' }],
}

// J) Market price — Change âm ra ngoặc đỏ `(47)` đồng bộ cashflows (chị Đẹp
// 2026-06-10); riêng 2 cột Diffs giữ dấu trừ (signed) vì là chênh lệch giá.
export const marketPriceTable: PnLTableData = {
  columns: [
    { key: 'month', label: 'Month', text: true },
    { key: 'last', label: 'Last' },
    { key: 'change', label: 'Change' },
    { key: 'g2', label: 'G2 FOB Diffs', signed: true, decimals: 2 },
    { key: 'pnl', label: 'FOB Diffs for PnL', signed: true, decimals: 2 },
    { key: 'date', label: 'Date', text: true, date: true },
  ],
  rows: [
    { month: 'LRCN26', last: 3472, change: -47, g2: -55, pnl: -55, date: '2026-05-27' },
    { month: 'LRCU26', last: 3339, change: -38, g2: 78, pnl: 78, date: '2026-05-27' },
    { month: 'LRCX26', last: 3262, change: -32, g2: 155, pnl: 155, date: '2026-05-27' },
    { month: 'LRCF27', last: 3189, change: -33, g2: 228, pnl: 228, date: '2026-05-27' },
    { month: 'LRCH27', last: 3146, change: -34, g2: 271, pnl: 271, date: '2026-05-27' },
    { month: 'LRCK27', last: 3118, change: -35, g2: 299, pnl: 299, date: '2026-05-27' },
    { month: 'LRCN27', last: 3102, change: -35, g2: 315, pnl: 315, date: '2026-05-27' },
    { month: 'LRCU27', last: 3090, change: -34, g2: 327, pnl: 327, date: '2026-05-27' },
    { month: 'LRCX27', last: 3078, change: -34, g2: 339, pnl: 339, date: '2026-05-27' },
    { month: 'LRCF28', last: 3040, change: -34, g2: 377, pnl: 377, date: '2026-05-27' },
  ],
}

// Supplier M2M KHÔNG có highlight nền đỏ — crosscheck visual.json PBI 2026-06-10:
// cột Total exp properties RỖNG (không CF); CF duy nhất = fontColor đỏ khi
// Room to buy < 0 (tô GIÁ TRỊ, đã cover bởi fmtNum số âm = ngoặc đỏ). Doc tay
// §4.2-E vẽ "tô nền đỏ" là SAI — đã gỡ supplierWarningHighlight.
